import * as EXIF from 'exif-js/exif';

import { ErrorWithRavenOptions } from './Error';
import { ImageFormat, isImageMimetype } from './FileFormats';
import { assert } from './Debug';
import { corsProxyUrl } from './Services';
import { getFileList } from './File';
import { isDataUri } from './String';
import { isNullOrZero } from './Number';
import { viewStartsWith } from './View';

import { safari } from '@egr/xbox/utils/UserAgent';

import { isNullOrEmpty, isNotNullOrEmpty } from '@egr/wcf/modules/utils/string';
import { fromDataUri } from '@egr/wcf/modules/utils/arraybuffer';

export function loadImage(src: string, tryViaProxyOnError: boolean = true): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>(
        (
            resolve: (value: HTMLImageElement | PromiseLike<HTMLImageElement>) => void,
            reject: (error: Error) => void
        ): void => {
            const image: HTMLImageElement = document.createElement('img');
            image.addEventListener(
                'load',
                (): void => {
                    resolve(image);
                }
            );
            image.addEventListener(
                'error',
                (errorEvent: ErrorEvent): void => {
                    const corsUrl: string | null = corsProxyUrl(src);
                    if (tryViaProxyOnError && corsUrl != null) {
                        resolve(loadImage(corsUrl, false));
                    } else {
                        reject(new ErrorWithRavenOptions(
                            errorEvent.message,
                            {
                                extra: {
                                    function: 'loadImage',
                                    src
                                }
                            }
                        ));
                    }
                }
            );
            image.crossOrigin = 'anonymous';
            image.src = src;
        }
    );
}

export interface ImageSize {
    width: number;
    height: number;
}

export interface ImagePosition {
    x: number;
    y: number;
}

export interface ImageSizeWithSrc extends ImageSize {
    src: string;
}

/**
 * Creates a object url from an ArrayBuffer and loads this url in temporary img-element to determine image size
 * If something fails, null is returned.
 * @param data
 */
export async function getImageBlobWithSize(data: ArrayBuffer): Promise<ImageSizeWithSrc | null> {
    const src: string = getObjectUrl(data);
    const size: ImageSize | null = await getImageSize(src);

    if (size == null) {
        return null;
    }

    return {
        src,
        ...size
    };
}

/**
 * Loads an url in a temporary img element to determine the image size.
 * If something fails, null is returned.
 * @param data
 */
export async function getImageSize(src: string): Promise<ImageSize | null> {
    try {
        const imageElement: HTMLImageElement = await loadImage(src);
        return {
            width: imageElement.width,
            height: imageElement.height
        };
    } catch {
        return null;
    }
}

/**
 * Creates a object url from an ArrayBuffer
 * @param data ArrayBuffer
 */
export function getObjectUrl(data: ArrayBuffer, type?: string): string {
    // in Edge18 new Blob(foo, undefined) throws error
    if (isNotNullOrEmpty(type)) {
        return URL.createObjectURL(
            new Blob(
                [new Uint8Array(data)],
                { type }
            )
        );
    }

    return URL.createObjectURL(
        new Blob([new Uint8Array(data)])
    );
}

export interface ImageSize {
    width: number;
    height: number;
}

export function getScaledImageRatio(src: ImageSize, target: Partial<ImageSize>): number {
    if (isNullOrZero(target.width) && isNullOrZero(target.height)) {
        return 1;
    }

    const expectedHeight: number = target.height === 0 ? src.height : (target.height ?? src.height);
    const expectedWidth: number = target.width === 0 ? src.width : (target.width ?? src.width);

    if (isNullOrZero(target.width)) {
        return expectedHeight / src.height;
    }

    if (isNullOrZero(target.height)) {
        return expectedWidth / src.width;
    }

    return Math.min(expectedHeight / src.height, expectedWidth / src.width);
}

export function getScaledImageSize(src: ImageSize, target: Partial<ImageSize>): ImageSize {
    const scaleRatio: number = getScaledImageRatio(src, target);
    return scaleImageSize(src, scaleRatio);
}

export function scaleImageSize(src: ImageSize, scaleRatio: number): ImageSize {
    return {
        width: Math.round(src.width * scaleRatio),
        height: Math.round(src.height * scaleRatio)
    };
}

const knownImageFormats: ReadonlyArray<ImageFormat> = [ImageFormat.JPG, ImageFormat.PNG, ImageFormat.GIF];
export function detectImageType(content: ArrayBuffer): ImageFormat | null {
    const view: Uint8Array = new Uint8Array(content);
    for (const format of knownImageFormats) {
        if (format.magicNumbers.length && viewStartsWith(view, format.magicNumbers)) {
            return format;
        }
    }

    return null;
}

/**
 * Adds an file input element to the DOM and calls click() on it.
 *
 * Eventlisteners for onchange and onerror are connected to that element and will resolve the function with null or the elements FileList
 *
 * Support:
 * - filetype restriction is not supported in Edge
 * - restriction 'image/*' is used by default here because image/png etc. is not supported in iOS-Safari
 * - multiple parameter is ignored in Android < 4.0
 * @param multiple allows selection of multiple files (default false)
 */
export function getImageFileList(multiple: boolean = false, accept: string = 'image/*'): Promise<FileList | null> {
    return getFileList(accept, multiple);
}

export function canvasToBlob(
    canvas?: HTMLCanvasElement & {msToBlob?: (() => Blob | null) | null},
    mimetype: string = ImageFormat.PNG.mimetype
): Promise<Blob | null> {
    if (canvas == null || !isImageMimetype(mimetype)) {
        return Promise.resolve(null);
    }

    if (
        canvas.toBlob != null &&
        // toBlob in safari only supports png and jpeg
        (!safari || [ImageFormat.PNG.mimetype, ImageFormat.JPG.mimetype].includes(mimetype))
    ) {
        return new Promise<Blob | null>((resolve: (returnValue: Blob | null) => void) => { canvas.toBlob(resolve, mimetype); });
    }

    if (canvas.msToBlob != null && mimetype === ImageFormat.PNG.mimetype) { // Note: msToBlob can only generate png images
        return Promise.resolve(canvas.msToBlob());
    }

    const dataUrl: string =  canvas.toDataURL(mimetype);
    const buffer: ArrayBuffer = fromDataUri(dataUrl);
    return Promise.resolve(new Blob([buffer], { type: mimetype}));
}

export function drawImage(
    imageElement: HTMLImageElement,
    width: number,
    height: number,
    background?: string,
    canvasWidth?: number,
    canvasHeight?: number
): HTMLCanvasElement | undefined {
    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = canvasWidth != null ? canvasWidth : width;
    canvas.height = canvasHeight != null ? canvasHeight : height;

    const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');

    if (ctx != null) {
        if (isNotNullOrEmpty(background)) {
            ctx.fillStyle = background;
            ctx.fillRect(0, 0, canvas.width, canvas.height);
        }
        ctx.drawImage(
            imageElement,
            (canvas.width - width) / 2,
            (canvas.height - height) / 2,
            width,
            height
        );

        return canvas;
    }

    return;
}

type DataUrlWithSize = {
    url: string;
    height: number;
    width: number;
} | {
    url: undefined;
    height?: number;
    width?: number;
};

export async function getDataUrlFromImageUri(
    imageUri: string,
    maxWidth: number = 0,
    maxHeight: number = 0,
    dataUrlMimeType: string = 'image/png',
    asSquare: boolean = false,
    background: string = '',
    quality?: number
): Promise<DataUrlWithSize> {
    if (isNullOrEmpty(imageUri)) {
        return { url: undefined };
    }

    try {
        const imageElement: HTMLImageElement = await loadImage(imageUri);

        if (imageElement.width === 0 && imageElement.height === 0) {
            imageElement.width = 100;
            imageElement.height = 100;
        }

        // skip generation if uri is already an data-url and no changes were requested
        if (
            maxWidth === 0 && maxHeight === 0 && !asSquare && background === '' &&
            isDataUri(imageUri)
        ) {
            return { url: imageUri, width: imageElement.width, height: imageElement.height };
        }

        const { width, height } = getScaledImageSize(imageElement, {
            width: maxWidth,
            height: maxHeight
        });

        const canvasSize: undefined | number = asSquare ? Math.max(height, width) : undefined;
        const canvas: HTMLCanvasElement | undefined = drawImage(
            imageElement,
            width,
            height,
            background,
            canvasSize,
            canvasSize
        );

        if (canvas != null) {
            return { url: canvas.toDataURL(dataUrlMimeType, quality), width, height };
        }
    } catch {
        return { url: undefined };
    }

    return { url: undefined };
}

export function getCenterPosition(src: ImageSize, target: ImageSize): ImagePosition {
    // FixMe: cases where the target property is lesser than the src property should be handled
    assert(target.width >= src.width, 'the width of the target should be greater than or equal to the with of the src');
    assert(target.height >= src.height, 'the height of the target should be greater than or equal to the height of the src');

    return {
        x: Math.floor((target.width - src.width) / 2),
        y: Math.floor((target.height - src.height) / 2)
    };
}

export async function resizedDataUrl(dataUrl: string, scale: number): Promise<string | null> {
    return new Promise<string | null>((resolve) => {
        if (scale === 1) {
            return resolve(dataUrl);
        }
        const imageElement: HTMLImageElement = new Image();
        imageElement.onload = () => {
            const canvas: HTMLCanvasElement = document.createElement('canvas');
            canvas.width = imageElement.width * scale;
            canvas.height = imageElement.height * scale;
            const context: CanvasRenderingContext2D = canvas.getContext('2d')!;
            context.scale(scale, scale);
            context.drawImage(imageElement, 0, 0);
            resolve(canvas.toDataURL());
        };
        imageElement.onerror = () => { resolve(null); };
        imageElement.src = dataUrl;
    });
}

export async function addDebugTexts(image: string, ...texts: Array<string>): Promise<string>;
export async function addDebugTexts(image: Blob, ...texts: Array<string>): Promise<Blob | null>;
export async function addDebugTexts(image: string | Blob, ...texts: Array<string>): Promise<Blob | string | null> {
    let img: HTMLImageElement;
    if (image instanceof Blob) {
        const url: string = URL.createObjectURL(image);
        img = await loadImage(url);
        URL.revokeObjectURL(url);
    } else {
        img = await loadImage(image);
    }

    const canvas: HTMLCanvasElement = document.createElement('canvas');
    canvas.width = img.width;
    canvas.height = img.height;

    const context: CanvasRenderingContext2D = canvas.getContext('2d')!;
    context.drawImage(img, 0, 0);

    context.fillStyle = '#ff0000';
    context.font = '30px serif';

    let start: number = 40;
    for (const text of texts) {
        context.fillText(text, 20, start);

        start += 40;
    }

    if (image instanceof Blob) {
        return canvasToBlob(canvas, image.type);
    }

    return canvas.toDataURL();
}

export async function imageUrlToCanvas(
    url: string | null | undefined,
    fixOrientation: boolean = false,
    maxCanvasSize?: Partial<ImageSize>
): Promise<HTMLCanvasElement | undefined> {
    if (isNullOrEmpty(url)) {
        return;
    }

    const imageElement: HTMLImageElement = await loadImage(url, true);
    const canvas: HTMLCanvasElement = document.createElement('canvas');

    canvas.width = imageElement.width;
    canvas.height = imageElement.height;

    const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
    if (ctx != null) {
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        if (fixOrientation) {
            await fixCanvasRotation(imageElement, canvas, maxCanvasSize?.width, maxCanvasSize?.height);
        } else {
            const canvasScaleRatio: number = maxCanvasSize != null ? getScaledImageRatio(imageElement, maxCanvasSize) : 1;
            const canvasSize: ImageSize = canvasScaleRatio < 1 ? scaleImageSize(imageElement, canvasScaleRatio) : imageElement;
            canvas.width = canvasSize.width;
            canvas.height = canvasSize.height;
            ctx.drawImage(imageElement, 0, 0, canvas.width, canvas.height);
        }
    }
    return canvas;
}

export async function imageUrlToBlob(
    url: string | null | undefined,
    mimetype: string = 'image/png',
    fixOrientation: boolean = false,
    maxCanvasSize?: Partial<ImageSize>
): Promise<Blob | null> {
    const canvas: HTMLCanvasElement | undefined = await imageUrlToCanvas(url, fixOrientation, maxCanvasSize);
    if (canvas != null) {
        return canvasToBlob(canvas, mimetype);
    }

    return null;
}

function getOrientation(imageElement: HTMLImageElement): Promise<number> {
    return new Promise<number>((resolve, reject) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        EXIF.getData(imageElement as any, function(): void {
            resolve(EXIF.getTag(imageElement, 'Orientation') || 0);
        });
    });
}

export async function fixCanvasRotation(
    imageElement: HTMLImageElement,
    canvas: HTMLCanvasElement,
    maxWidth: number = 0,
    maxHeight: number = 0,
): Promise<void> {
    const imageRotation: number = await getOrientation(imageElement);

    const beforeWidth: number = canvas.width;
    const beforeHeight: number = canvas.height;

    const scaleRatio: number = Math.min(getScaledImageRatio(imageElement, {width: maxWidth, height: maxHeight}), 1);

    switch (imageRotation) {
        case 5:
        case 6:
        case 7:
        case 8:
            canvas.width = beforeHeight * scaleRatio;
            canvas.height = beforeWidth * scaleRatio;
            break;
        default:
            canvas.width = beforeWidth * scaleRatio;
            canvas.height = beforeHeight * scaleRatio;
            break;
    }

    const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
    if (ctx != null) {
        switch (imageRotation) {
            case 2:
                // horizontal flip
                ctx.translate(beforeWidth * scaleRatio, 0);
                ctx.scale(-1, 1);
                break;
            case 3:
                // 180° rotate left
                ctx.translate(beforeWidth * scaleRatio, beforeHeight * scaleRatio);
                ctx.rotate(Math.PI);
                break;
            case 4:
                // vertical flip
                ctx.translate(0, beforeHeight * scaleRatio);
                ctx.scale(1, -1);
                break;
            case 5:
                // vertical flip + 90 rotate right
                ctx.rotate(0.5 * Math.PI);
                ctx.scale(1, -1);
                break;
            case 6:
                // 90° rotate right
                ctx.rotate(0.5 * Math.PI);
                ctx.translate(0, -beforeHeight * scaleRatio);
                break;
            case 7:
                // horizontal flip + 90 rotate right
                ctx.rotate(0.5 * Math.PI);
                ctx.translate(beforeWidth * scaleRatio, -beforeHeight * scaleRatio);
                ctx.scale(-1, 1);
                break;
            case 8:
                // 90° rotate left
                ctx.rotate(-0.5 * Math.PI);
                ctx.translate(-beforeWidth * scaleRatio, 0);
                break;
            default:
                break;
        }

        ctx.drawImage(imageElement, 0, 0, beforeWidth * scaleRatio, beforeHeight * scaleRatio);
    }
}

export async function getDataUrlFromImageFile(imageFile: File, maxWidth?: number, maxHeight?: number): Promise<string | undefined> {
    if (maxWidth == null && maxHeight == null) {
        return new Promise<string | undefined>((resolve) => {
            const fr: FileReader = new FileReader();
            fr.onerror = (event: ProgressEvent) => {
                resolve(undefined);
            };
            fr.onload = () => {
                resolve(fr.result as string | undefined);
            };
            fr.readAsDataURL(imageFile);
        });
    }
    const canvas: HTMLCanvasElement | undefined = await getCanvasFromImageFile(imageFile, maxWidth, maxHeight);
    if (canvas == null) {
        return;
    }
    return canvas.toDataURL(imageFile.type, 0.9);
}

export async function getCanvasFromImageFile(imageFile: File | null , maxWidth?: number, maxHeight?: number): Promise<HTMLCanvasElement | undefined> {
    if (imageFile == null) {
        return;
    }

    const url: string = URL.createObjectURL(imageFile);
    try {
        return await imageUrlToCanvas(
            url,
            imageFile.type === 'image/jpeg',
            {
                width: maxWidth,
                height: maxHeight
            }
        );
    } finally {
        URL.revokeObjectURL(url);
    }
}